// Resolving web-unfriendly originals (today: HEIC) to the JPEG derivatives the backend produced.
//
// The backend does NOT rewrite attachments. It leaves `buyerAttachments` / `supplierAttachments`
// exactly as uploaded and hangs a sibling list off the entity:
//
//   workOrder.convertedImages: [{ fileId, s3ObjectKey, convertedS3Key, convertedUrl, convertedThumbUrl }]
//
// Each ref identifies its original by EITHER `fileId` (the `{fileId, fileName}` attachment shape)
// or `s3ObjectKey` (the bare-key and image-row shapes) — whichever the attachment column stores.
// Only `ready` conversions are ever sent, and the list is absent for the overwhelming majority of
// entities, so absence is the normal case and must never be treated as an error.
//
// The one rule every caller depends on: **fall back to the original whenever there is no usable
// conversion.** An unconverted file then renders exactly as it does today, which is what makes it
// safe to wire this into some surfaces and not others.

export interface ConvertedImageRef {
	fileId?: string | null;
	s3ObjectKey?: string | null;
	convertedS3Key?: string | null;
	convertedUrl?: string | null;
	convertedThumbUrl?: string | null;
}

export interface ConvertedImageIndex {
	byFileId: Map<string, ConvertedImageRef>;
	byS3ObjectKey: Map<string, ConvertedImageRef>;
	isEmpty: boolean;
}

export const EMPTY_CONVERTED_IMAGE_INDEX: ConvertedImageIndex = {
	byFileId: new Map(),
	byS3ObjectKey: new Map(),
	isEmpty: true,
};

// `convertedUrl` is only populated for buckets that sit behind CloudFront. When it is absent the
// derivative exists in S3 but this client has no way to reach it, so the correct move is to behave
// as though there were no conversion at all rather than render a broken image.
const hasUsableConversion = (ref?: ConvertedImageRef | null): boolean =>
	!!ref && typeof ref.convertedUrl === 'string' && ref.convertedUrl.length > 0;

export const buildConvertedImageIndex = (
	convertedImages?: ConvertedImageRef[] | null
): ConvertedImageIndex => {
	const refs = convertedImages || [];
	if (refs.length === 0) {
		return EMPTY_CONVERTED_IMAGE_INDEX;
	}
	const byFileId = new Map<string, ConvertedImageRef>();
	const byS3ObjectKey = new Map<string, ConvertedImageRef>();
	refs.forEach((ref) => {
		if (!hasUsableConversion(ref)) {
			return;
		}
		if (ref.fileId) {
			byFileId.set(ref.fileId, ref);
		}
		if (ref.s3ObjectKey) {
			byS3ObjectKey.set(ref.s3ObjectKey, ref);
		}
	});
	return {
		byFileId,
		byS3ObjectKey,
		isEmpty: byFileId.size === 0 && byS3ObjectKey.size === 0,
	};
};

// Combines several `convertedImages` lists into one.
//
// Needed because a single view can show attachments drawn from more than one work order — a
// sub-contracted WO renders its parent's and its sub's files side by side, and each of those
// carries its own conversions. Merging is safe rather than merely convenient: every ref is keyed by
// a globally unique random fileId, so a ref whose attachment is not on screen simply never matches,
// and an attachment with no ref falls back to its original. There is no way to resolve to someone
// else's image.
export const mergeConvertedImages = (
	...lists: (ConvertedImageRef[] | null | undefined)[]
): ConvertedImageRef[] => {
	const merged = lists.reduce<ConvertedImageRef[]>(
		(accumulated, list) => (list && list.length ? accumulated.concat(list) : accumulated),
		[]
	);
	return merged;
};

export const findConvertedImageRef = (
	index: ConvertedImageIndex,
	attachment
): ConvertedImageRef | null => {
	if (!index || index.isEmpty || !attachment) {
		return null;
	}
	const fileId = attachment.fileId;
	if (fileId && index.byFileId.has(fileId)) {
		return index.byFileId.get(fileId) as ConvertedImageRef;
	}
	const s3ObjectKey = attachment.s3ObjectKey;
	if (s3ObjectKey && index.byS3ObjectKey.has(s3ObjectKey)) {
		return index.byS3ObjectKey.get(s3ObjectKey) as ConvertedImageRef;
	}
	return null;
};

// The derivative's own file name, taken from the last segment of its S3 key.
//
// This is what makes extension-driven code paths work without teaching any of them about HEIC:
// a converted `IMG_0042.HEIC` reports `IMG_0042.jpg`, so file-type classification, icon choice and
// viewer routing all reach the right answer on their existing rules. An UNconverted HEIC keeps its
// original name and stays classified exactly as it is today — no behaviour change, no new risk.
export const getConvertedFileName = (ref?: ConvertedImageRef | null): string | null => {
	const convertedS3Key = ref && ref.convertedS3Key;
	if (!convertedS3Key) {
		return null;
	}
	const lastSegment = convertedS3Key.split('/').pop();
	return lastSegment || null;
};

export interface ResolvedAttachment {
	fileId?: string;
	fileName?: string;
	// The name to classify/label by: the derivative's when converted, the original's otherwise.
	displayFileName: string;
	// The URL to render: the derivative's when converted, null otherwise so the caller keeps
	// building the original's URL the way it always has.
	convertedUrl: string | null;
	convertedThumbUrl: string | null;
	isConverted: boolean;
}

// Decorates one attachment with its conversion, if it has one. The original fields are preserved
// untouched, so a resolved attachment is still a valid attachment — but do NOT persist one: the
// added fields are derived, and writing them back would put stale URLs in the database.
export const resolveAttachment = (attachment, index: ConvertedImageIndex): ResolvedAttachment => {
	const ref = findConvertedImageRef(index, attachment);
	const convertedFileName = getConvertedFileName(ref);
	if (!ref || !convertedFileName) {
		return {
			...attachment,
			displayFileName: attachment && attachment.fileName,
			convertedUrl: null,
			convertedThumbUrl: null,
			isConverted: false,
		};
	}
	return {
		...attachment,
		displayFileName: convertedFileName,
		convertedUrl: ref.convertedUrl as string,
		// The thumbnail is optional even on a ready conversion; fall through to the full-size
		// derivative rather than back to the undecodable original.
		convertedThumbUrl: ref.convertedThumbUrl || (ref.convertedUrl as string),
		isConverted: true,
	};
};

// The entry point for a list: takes the attachments and the entity's `convertedImages` straight
// off the API response, and hands back one resolved entry per attachment, in order.
export const resolveAttachments = (
	attachments,
	convertedImages?: ConvertedImageRef[] | null
): ResolvedAttachment[] => {
	const index = buildConvertedImageIndex(convertedImages);
	return (attachments || []).map((attachment) => resolveAttachment(attachment, index));
};
